import {ENDPOINTS} from '../common/endpoints';
import {normalizeOrderGroupsGuid, normalizeOrderGuid, toOrderWasteGuidQueryParams} from '../mappers/orderGuid.mapper';
import {
OrderCompletionGuidResult,
OrderGuidDto,OrderSearchGuidRequest,OrdersGroupByDayGuidDto,OrderWasteBatchResultGuidDto,
OrderWasteEntryGuidRequest,
OrderWasteLogGuidDto,
SaveOrderGuidRequest
} from '@mybake/ui/models/Order/IOrderGuid';
import {baseAPI} from './BaseAPI';
import {TAGS} from "./TagTypes";

export const orderAPI = baseAPI.injectEndpoints({
    endpoints: (build) => ({
        getWorkshopDates: build.query<string[], void>({
            query: () => ({url: ENDPOINTS.ORDERS.WORKSHOP_DATES, method: 'GET'}),
            providesTags: [TAGS.ORDER, TAGS.ORDER_V2],
        }),
        getOrdersV2: build.query<OrdersGroupByDayGuidDto[], boolean | void>({
            query: includeClosed => ({
                url: ENDPOINTS.ORDERS.V2,
                method: 'GET',
                params: {includeClosed: includeClosed ?? false},
            }),
            transformResponse: normalizeOrderGroupsGuid,
            providesTags: () => [{type: TAGS.ORDER_V2, id: 'LIST'}],
        }),
        searchOrdersV2: build.query<OrdersGroupByDayGuidDto[], OrderSearchGuidRequest>({
            query: params => ({url: ENDPOINTS.ORDERS.V2_SEARCH, method: 'GET', params}),
            transformResponse: normalizeOrderGroupsGuid,
            providesTags: () => [{type: TAGS.ORDER_V2, id: 'SEARCH'}],
        }),
        hasUnreviewedOrdersV2: build.query<boolean, void>({
            query: () => ({url: ENDPOINTS.ORDERS.V2_HAS_UNREVIEWED, method: 'GET'}),
            providesTags: [TAGS.UNREVIEWED_ORDERS],
        }),
        getOrderV2: build.query<OrderGuidDto, string>({
            query: id => ({url: `${ENDPOINTS.ORDERS.V2}/${id}`, method: 'GET'}),
            transformResponse: normalizeOrderGuid,
            providesTags: (_result, _error, id) => [{type: TAGS.ORDER_V2, id}],
        }),
        createOrderV2: build.mutation<OrderGuidDto, SaveOrderGuidRequest>({
            query: body => ({url: ENDPOINTS.ORDERS.V2, method: 'POST', body}),
            transformResponse: normalizeOrderGuid,
            invalidatesTags: [
                {type: TAGS.ORDER_V2, id: 'LIST'},
                {type: TAGS.ORDER_V2, id: 'SEARCH'},
                TAGS.CUSTOMERS,
                TAGS.FINANCES, TAGS.ANALYTICS,
            ],
        }),
        updateOrderV2: build.mutation<OrderGuidDto, {id: string; body: SaveOrderGuidRequest}>({
            query: ({id, body}) => ({url: `${ENDPOINTS.ORDERS.V2}/${id}`, method: 'PUT', body}),
            transformResponse: normalizeOrderGuid,
            invalidatesTags: (_result, _error, {id}) => [
                {type: TAGS.ORDER_V2, id},
                {type: TAGS.ORDER_V2, id: 'LIST'},
                {type: TAGS.ORDER_V2, id: 'SEARCH'},
                TAGS.CUSTOMERS,
                TAGS.FINANCES, TAGS.ANALYTICS,
            ],
        }),
        deleteOrderV2: build.mutation<void, {id: string; canceledByCustomer: boolean}>({
            query: ({id, canceledByCustomer}) => ({
                url: `${ENDPOINTS.ORDERS.V2}/${id}`,
                method: 'DELETE',
                params: {canceledByCustomer},
            }),
            invalidatesTags: (_result, _error, {id}) => [
                {type: TAGS.ORDER_V2, id},
                {type: TAGS.ORDER_V2, id: 'LIST'},
                {type: TAGS.ORDER_V2, id: 'SEARCH'},
                TAGS.CUSTOMERS,
            ],
        }),
        completeOrderV2: build.mutation<OrderCompletionGuidResult, {id: string; simulate?: boolean; allowNegativeStock?: boolean}>({
            query: ({id, simulate, allowNegativeStock}) => ({
                url: `${ENDPOINTS.ORDERS.V2}/${id}/completion`,
                method: 'POST',
                params: {simulate: simulate ?? false, allowNegativeStock: allowNegativeStock ?? false},
            }),
            invalidatesTags: (_result, _error, request) => request.simulate ? [] : [
                {type: TAGS.ORDER_V2, id: request.id},
                {type: TAGS.ORDER_V2, id: 'LIST'},
                {type: TAGS.ORDER_V2, id: 'SEARCH'},
                TAGS.ITEM,
                TAGS.PACKAGES,
                TAGS.FINANCES, TAGS.ANALYTICS,
            ],
        }),
        closeOrderV2: build.mutation<OrderGuidDto, {id: string; isReceiptIssued: boolean}>({
            query: ({id, isReceiptIssued}) => ({
                url: `${ENDPOINTS.ORDERS.V2}/${id}/close`,
                method: 'POST',
                params: {isReceiptIssued},
            }),
            transformResponse: normalizeOrderGuid,
            invalidatesTags: (_result, _error, {id}) => [
                {type: TAGS.ORDER_V2, id},
                {type: TAGS.ORDER_V2, id: 'LIST'},
                {type: TAGS.ORDER_V2, id: 'SEARCH'},
                TAGS.UNREVIEWED_ORDERS,
                TAGS.FINANCES, TAGS.ANALYTICS,
            ],
        }),
        reviewOrderV2: build.mutation<void, string>({
            query: id => ({url: `${ENDPOINTS.ORDERS.V2}/${id}/review`, method: 'POST'}),
            invalidatesTags: (_result, _error, id) => [
                {type: TAGS.ORDER_V2, id},
                {type: TAGS.ORDER_V2, id: 'LIST'},
                {type: TAGS.ORDER_V2, id: 'SEARCH'},
                TAGS.UNREVIEWED_ORDERS,
            ],
        }),
        getOrderWasteV2: build.query<OrderWasteLogGuidDto[], string>({
            query: orderId => ({url: `${ENDPOINTS.ORDERS.V2}/${orderId}/waste`, method: 'GET'}),
            providesTags: (_result, _error, orderId) => [{type: TAGS.ORDER_V2, id: `waste-${orderId}`}],
        }),
        processOrderWasteBatchV2: build.mutation<OrderWasteBatchResultGuidDto, {orderId: string; entries: OrderWasteEntryGuidRequest[]; simulate: boolean; allowNegativeStock?: boolean}>({
            query: ({orderId, entries, simulate, allowNegativeStock}) => ({
                url: `${ENDPOINTS.ORDERS.V2}/${orderId}/waste/batch`,
                method: 'POST',
                params: toOrderWasteGuidQueryParams(simulate, allowNegativeStock),
                body: {entries},
            }),
            invalidatesTags: (_result, _error, request) => request.simulate ? [] : [
                {type: TAGS.ORDER_V2, id: request.orderId},
                {type: TAGS.ORDER_V2, id: `waste-${request.orderId}`},
                {type: TAGS.ORDER_V2, id: 'LIST'},
                {type: TAGS.ORDER_V2, id: 'SEARCH'},
                TAGS.ITEM,
                TAGS.PACKAGES,
                TAGS.FINANCES, TAGS.ANALYTICS,
            ],
        }),
    }),
});
